import React, { useEffect, useState } from 'react';
import { analyticsAPI, queueAPI } from '../services/api.jsx';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from '../components/ConfirmModal.jsx';
import './Dashboard.css';

const MarketingDashboard = () => {
  const { hasPermission, hasAnyPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [templateCount, setTemplateCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clearing, setClearing] = useState(false);
  const [clearMessage, setClearMessage] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const canViewCampaigns = hasAnyPermission(['campaigns:read:own', 'campaigns:read:any']);
  const canViewContacts = hasAnyPermission(['contacts:read:own', 'contacts:read:any']);
  const canViewTemplates = hasAnyPermission(['templates:read:own', 'templates:read:any']);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [dashboardResponse, templatesResponse] = await Promise.all([
        analyticsAPI.getDashboard(),
        import('../services/api.jsx').then(m => m.default.get('/templates'))
      ]);
      setStats(dashboardResponse.data);
      // Count total templates
      if (Array.isArray(templatesResponse.data)) {
        setTemplateCount(templatesResponse.data.length);
      }
    } catch (err) {
      setError('Failed to load marketing data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearQueue = async () => {
    try {
      setClearing(true);
      setClearMessage('');
      const response = await queueAPI.clear();
      setClearMessage(`✅ ${response.data.message} (${response.data.logsCleared} database logs cleared, queue purged)`);
      // Reload dashboard to update stats
      await loadDashboard();
    } catch (err) {
      setClearMessage('❌ Failed to clear queue: ' + (err.response?.data?.error || err.message));
      console.error(err);
    } finally {
      setClearing(false);
      setShowClearConfirm(false);
    }
  };

  if (loading) return <div className="loading">Loading marketing dashboard...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="container">
      <ConfirmModal
        show={showClearConfirm}
        title="Clear Queue"
        message="This will clear all queued and failed email logs, plus any stuck messages in the queue. Continue?"
        onConfirm={handleClearQueue}
        onCancel={() => setShowClearConfirm(false)}
        confirmText="Clear Queue"
        confirmStyle="danger"
      />

      <h1>Marketing Dashboard</h1>
      
      {/* Main Marketing Stats */}
      <div className="stats-grid">
        {canViewCampaigns && (
          <div className="stat-card">
            <div className="stat-value">{stats?.totalCampaigns || 0}</div>
            <div className="stat-label">Total Campaigns</div>
          </div>
        )}
        
        {canViewContacts && (
          <div className="stat-card">
            <div className="stat-value">{stats?.totalContacts || 0}</div>
            <div className="stat-label">Total Contacts</div>
          </div>
        )}
        
        {(canViewCampaigns || canViewContacts) && (
          <div className="stat-card">
            <div className="stat-value">{stats?.totalEmailsSent || 0}</div>
            <div className="stat-label">Emails Sent</div>
          </div>
        )}
        
        {(canViewCampaigns || canViewContacts) && (
          <div className="stat-card">
            <div className="stat-value">{stats?.overallOpenRate || 0}%</div>
            <div className="stat-label">Open Rate</div>
          </div>
        )}
      </div>

      {/* Engagement Overview */}
      {(canViewCampaigns || canViewContacts) && (
        <div className="card">
          <h2>Engagement Overview</h2>
          <div className="engagement-stats">
            <div className="engagement-row">
              <span>Total Opened:</span>
              <strong>{stats?.totalOpened || 0}</strong>
            </div>
            <div className="engagement-row">
              <span>Total Clicked:</span>
              <strong>{stats?.totalClicked || 0}</strong>
            </div>
            <div className="engagement-row">
              <span>Click Rate:</span>
              <strong>{stats?.overallClickRate || 0}%</strong>
            </div>
          </div>
        </div>
      )}

      {/* Queue Status */}
      {canViewCampaigns && ((stats?.totalQueued || 0) > 0 || (stats?.totalFailed || 0) > 0) && (
        <div className="card">
          <h2>Queue Status</h2>
          <div style={{ display: 'flex', gap: '2rem' }}>
            <div>
              <strong style={{ fontSize: '1.5rem', color: '#f0ad4e' }}>{stats?.totalQueued || 0}</strong>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>Queued Emails</div>
            </div>
            <div>
              <strong style={{ fontSize: '1.5rem', color: '#d9534f' }}>{stats?.totalFailed || 0}</strong>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>Failed Emails</div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Queue Button */}
      {(canViewCampaigns || canViewContacts) && (
        <div className="card">
          <button 
            onClick={() => setShowClearConfirm(true)} 
            className="btn btn-secondary"
            disabled={clearing}
            title="Clear all queued and failed emails"
          >
            {clearing ? '🔄 Clearing Queue...' : '🗑️ Clear Queue'}
          </button>
          
          {clearMessage && (
            <div style={{ marginTop: '1rem', padding: '0.5rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
              {clearMessage}
            </div>
          )}
        </div>
      )}

      {/* Marketing Summary Section */}
      {(canViewContacts || canViewTemplates || canViewCampaigns) && (
        <div className="card">
          <h2>Marketing Summary</h2>
          <div className="summary-grid">
            {canViewContacts && (
              <div className="summary-item">
                <div className="summary-icon">👥</div>
                <div className="summary-content">
                  <div className="summary-label">Contacts</div>
                  <div className="summary-value">{stats?.totalContacts || 0}</div>
                  <a href="/contacts" className="summary-link">Manage Contacts →</a>
                </div>
              </div>
            )}
            
            {canViewTemplates && (
              <div className="summary-item">
                <div className="summary-icon">📝</div>
                <div className="summary-content">
                  <div className="summary-label">Templates</div>
                  <div className="summary-value">{templateCount}</div>
                  <a href="/templates" className="summary-link">Manage Templates →</a>
                </div>
              </div>
            )}
            
            {canViewCampaigns && (
              <div className="summary-item">
                <div className="summary-icon">📬</div>
                <div className="summary-content">
                  <div className="summary-label">Campaigns</div>
                  <div className="summary-value">{stats?.totalCampaigns || 0}</div>
                  <a href="/campaigns" className="summary-link">View Campaigns →</a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingDashboard;
